/**
 * Lazy loads hover videos on product cards
 * Videos are loaded only when:
 * - User hovers over the card (with debouncing)
 * - Card is near viewport (for preloading)
 */
class CardProductVideoLoader {
  constructor() {
    this.loadedVideos = new Set();
    this.hoverTimeouts = new Map();
    this.debounceDelay = 100; // ms
    this.preloadMargin = '600px'; // Preload videos for cards within 600px of viewport
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Find all product cards with hover videos
    const cards = document.querySelectorAll('.card__hover-image[data-hover-media-type="video"], .card__hover-image[data-hover-media-type="external_video"]');
    
    if (cards.length === 0) return;

    // Filter out cards that have mobile hover disabled
    const enabledCards = Array.from(cards).filter(card => {
      const cardWrapper = card.closest('.product-card-wrapper');
      if (!cardWrapper) return false;
      
      // Check if mobile hover is disabled
      const enableMobileHover = cardWrapper.getAttribute('data-enable-mobile-hover');
      if (enableMobileHover === 'false' && this.isMobileDevice()) {
        return false;
      }
      return true;
    });

    if (enabledCards.length === 0) return;

    // Setup IntersectionObserver for viewport-based preloading
    this.setupIntersectionObserver(enabledCards);

    // Setup hover event listeners
    this.setupHoverListeners(enabledCards);
  }

  isMobileDevice() {
    // Check for touch device and/or small screen width
    return window.innerWidth <= 768 || ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }

  setupIntersectionObserver(cards) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const hoverImage = entry.target;
            const mediaId = hoverImage.getAttribute('data-hover-media-id');
            
            // Preload video when card is near viewport
            if (mediaId && !this.loadedVideos.has(mediaId)) {
              this.preloadVideo(hoverImage, mediaId);
            }
          }
        });
      },
      {
        rootMargin: this.preloadMargin,
        threshold: 0.01
      }
    );

    // Observe each card's parent wrapper
    cards.forEach((card) => {
      const cardWrapper = card.closest('.product-card-wrapper');
      if (cardWrapper) {
        observer.observe(cardWrapper);
      }
    });
  }

  setupHoverListeners(cards) {
    cards.forEach((card) => {
      const cardWrapper = card.closest('.product-card-wrapper');
      if (!cardWrapper) return;

      const mediaId = card.getAttribute('data-hover-media-id');
      if (!mediaId) return;

      // Mouse enter - load video with debouncing
      cardWrapper.addEventListener('mouseenter', () => {
        if (this.loadedVideos.has(mediaId)) return;

        // Clear any existing timeout
        if (this.hoverTimeouts.has(mediaId)) {
          clearTimeout(this.hoverTimeouts.get(mediaId));
        }

        // Debounce the video loading
        const timeout = setTimeout(() => {
          this.loadVideo(card, mediaId);
          this.hoverTimeouts.delete(mediaId);
        }, this.debounceDelay);

        this.hoverTimeouts.set(mediaId, timeout);
      });

      // Mouse leave - cancel pending load if user leaves quickly
      cardWrapper.addEventListener('mouseleave', () => {
        if (this.hoverTimeouts.has(mediaId)) {
          clearTimeout(this.hoverTimeouts.get(mediaId));
          this.hoverTimeouts.delete(mediaId);
        }
      });
    });
  }

  preloadVideo(hoverImage, mediaId) {
    // Preload video but don't play it yet
    this.loadVideo(hoverImage, mediaId, false);
  }

  loadVideo(hoverImage, mediaId, shouldPlay = true) {
    // Check if already loaded
    if (this.loadedVideos.has(mediaId)) {
      if (shouldPlay) {
        this.playVideo(hoverImage);
      }
      return;
    }

    // Find the template
    const template = document.getElementById(`hover-video-template-${mediaId}`);
    if (!template) return;

    // Clone template content
    const content = template.content.cloneNode(true);
    const videoContainer = content.querySelector('.media');
    
    if (!videoContainer) return;

    // Append to hover image container
    hoverImage.appendChild(videoContainer);

    // Mark as loaded
    this.loadedVideos.add(mediaId);

    // Handle video playback
    if (shouldPlay) {
      // Small delay to ensure video element is in DOM
      requestAnimationFrame(() => {
        this.playVideo(hoverImage);
      });
    }
  }

  playVideo(hoverImage) {
    const video = hoverImage.querySelector('video');
    const iframe = hoverImage.querySelector('iframe');
    const poster = hoverImage.querySelector('.card__hover-video-poster');

    if (video) {
      // For regular video elements
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      
      // Hide poster when video starts playing
      if (poster) {
        video.addEventListener('playing', () => {
          poster.style.opacity = '0';
          setTimeout(() => {
            poster.style.display = 'none';
          }, 300); // Match CSS transition duration
        }, { once: true });
      }
      
      // Force play for Safari
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // Autoplay was prevented, which is fine
        });
      }
    } else if (iframe) {
      // For external videos (YouTube, Vimeo), they should autoplay via URL params
      // Hide poster after a short delay to allow iframe to load
      if (poster) {
        setTimeout(() => {
          poster.style.opacity = '0';
          setTimeout(() => {
            poster.style.display = 'none';
          }, 300); // Match CSS transition duration
        }, 500); // Give iframe time to start loading
      }
    }
  }
}

// Initialize on page load
if (!window.cardProductVideoLoader) {
  window.cardProductVideoLoader = new CardProductVideoLoader();
}

// Re-initialize for dynamically loaded content (e.g., AJAX product grids)
document.addEventListener('shopify:section:load', () => {
  if (window.cardProductVideoLoader) {
    window.cardProductVideoLoader.setup();
  }
});

// Also handle theme editor updates
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    if (window.cardProductVideoLoader) {
      window.cardProductVideoLoader.setup();
    }
  });
}

